import { useEffect, useState } from 'react'
import './FloatingActionButton.css'

export interface FloatingActionButtonProps {
  icon: string
  label: string
  onClick: () => void
  disabled?: boolean
  position?: { bottom: number; right: number }
  haptic?: boolean
  variant?: 'primary' | 'success' | 'warning' | 'error'
}

export function FloatingActionButton({
  icon,
  label,
  onClick,
  disabled = false,
  position = { bottom: 80, right: 16 },
  haptic = true,
  variant = 'primary'
}: FloatingActionButtonProps) {
  const [showLabel, setShowLabel] = useState(false)
  const [pressing, setPressing] = useState(false)

  const handleClick = () => {
    if (disabled) return

    // Haptic feedback on mobile
    if (haptic && 'vibrate' in navigator) {
      navigator.vibrate(10)
    }

    onClick()
  }

  const handleTouchStart = () => {
    setPressing(true)
    setShowLabel(true)
  }

  const handleTouchEnd = () => {
    setPressing(false)
    setTimeout(() => setShowLabel(false), 200)
  }

  const handleMouseEnter = () => {
    setShowLabel(true)
  }

  const handleMouseLeave = () => {
    setShowLabel(false)
  }

  // Hide label after a few seconds
  useEffect(() => {
    if (showLabel) {
      const timer = setTimeout(() => setShowLabel(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [showLabel])

  return (
    <button
      className={`floating-action-button ${variant} ${disabled ? 'disabled' : ''} ${pressing ? 'pressing' : ''}`}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
      style={{
        bottom: `${position.bottom}px`,
        right: `${position.right}px`
      }}
      aria-label={label}
    >
      <span className="fab-icon">{icon}</span>

      {showLabel && (
        <span className="fab-label">{label}</span>
      )}
    </button>
  )
}
